/**
 * UI → viewport sürüklemesinin **kaynak** tarafı (paneldeki katalog öğesi).
 *
 * Bugüne kadar her ürün bunu kendi yöntemiyle yapıyordu:
 *  - room designer: `SUGAR_PRODUCT_MIME` + `beginProductDrag()` çağrısı
 *  - configurator: `dataTransfer.setData("data-model", …)` + modül seviyesinde
 *    `setConfiguratorPendingDragModel` yedeği
 *
 * İkisi de aynı problemi ayrı ayrı çözüyordu. Standart burası.
 */

export const SUGAR_ITEM_MIME = "application/x-sugar-item";

/** Sürüklenen öğe. `kind` ürünün kendi ayrımı içindir. */
export interface SugarItemDragPayload<T = unknown> {
  readonly kind: string;
  readonly data: T;
}

/**
 * Aynı doküman içi yedek.
 *
 * Tarayıcılar `dragover` sırasında `dataTransfer.getData()` okumaya izin
 * vermez — veri yalnızca `drop` anında okunabilir. Ama sürüklerken hayalet
 * modeli sahnede gezdirmek için veriye `dragover`'da ihtiyaç var. Bu yüzden
 * yük burada da tutuluyor.
 */
let pending: SugarItemDragPayload | undefined;
/** Bu sürüklemede önizleme sahiplenildi mi (bkz. claimItemDragPayload). */
let claimed = false;

let transparentImage: HTMLImageElement | undefined;

function getTransparentDragImage(): HTMLImageElement {
  if (!transparentImage) {
    transparentImage = new Image();
    transparentImage.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  }
  return transparentImage;
}

export interface StartItemDragOptions {
  /** Varsayılan `"copy"`. Room designer taşıma için `"move"` kullanıyor. */
  effect?: DataTransferEffect;
  /**
   * Tarayıcının kendi sürükleme görselini gizle (sahnedeki hayalet model
   * yeterli olduğunda). Varsayılan `true`.
   */
  hideDragImage?: boolean;
}

export type DataTransferEffect = "copy" | "move" | "link" | "none";

/**
 * Panelin `dragstart` handler'ında çağrılır.
 *
 * ```ts
 * <div draggable="true"
 *      @dragstart=${(e: DragEvent) => startItemDrag(e, { kind: "product", data: product })}
 *      @dragend=${endItemDrag}>
 * ```
 */
export function startItemDrag<T>(
  event: DragEvent,
  payload: SugarItemDragPayload<T>,
  options: StartItemDragOptions = {},
): void {
  pending = payload as SugarItemDragPayload;
  claimed = false;

  const dt = event.dataTransfer;
  if (dt) {
    dt.clearData();
    try {
      dt.setData(SUGAR_ITEM_MIME, JSON.stringify(payload));
    } catch {
      // Serileşemeyen yük (ör. döngüsel referans) — yedek zaten devrede.
    }
    // Bazı tarayıcılar özel MIME'ı yok sayar; text/plain her yerde çalışır.
    dt.setData("text/plain", payload.kind);
    dt.effectAllowed = options.effect ?? "copy";
    if (options.hideDragImage !== false) {
      dt.setDragImage(getTransparentDragImage(), 0, 0);
    }
  }
}

/** Panelin `dragend` handler'ında çağrılır. Yedeği temizler. */
export function endItemDrag(): void {
  pending = undefined;
  claimed = false;
}

/**
 * Yükü `dragstart` dışında ayarlamak gerekirse (ör. imperatif bir
 * "sürüklemeyi başlat" API'si).
 */
export function setItemDragPayload(payload: SugarItemDragPayload): void {
  pending = payload;
  claimed = false;
}

/** Yükü tüketmeden okur. */
export function peekItemDragPayload(): SugarItemDragPayload | undefined {
  return pending;
}

/**
 * Yükü "sahiplen": bir kere sahiplenilir, ama **silinmez**.
 *
 * Önizleme (hayalet model) yaratıldıktan sonra çağrılır; ikinci kez çağrılırsa
 * `undefined` döner, böylece aynı sürüklemede iki önizleme oluşmaz.
 *
 * Yükün sürükleme boyunca okunabilir kalması şart: tarayıcı `dragover`
 * sırasında `dataTransfer`'ı okutmadığı için drop yüzeyinin "bu sürükleme bana
 * mı ait" sorusunu cevaplayabildiği tek kaynak burası. Silinirse `dragover`
 * `preventDefault()` çağıramaz ve `drop` hiç gelmez.
 *
 * Gerçekten silmek için `endItemDrag()` (dragend / drop).
 */
export function claimItemDragPayload(): SugarItemDragPayload | undefined {
  if (!pending || claimed) return undefined;
  claimed = true;
  return pending;
}

/** Bu sürükleme için önizleme sahiplenildi mi. */
export function isItemDragClaimed(): boolean {
  return claimed;
}

/**
 * Viewport tarafında yükü okur. Önce `dataTransfer`, olmazsa aynı doküman
 * yedeği. `dragover` sırasında pratikte hep yedekten gelir.
 */
export function readItemDragPayload(
  event: DragEvent,
): SugarItemDragPayload | undefined {
  const raw = event.dataTransfer?.getData(SUGAR_ITEM_MIME);
  if (raw) {
    try {
      return JSON.parse(raw) as SugarItemDragPayload;
    } catch {
      // düş ve yedeği kullan
    }
  }
  return pending;
}

/** Sürükleme şu an aktif mi (yedeğe göre). */
export function hasPendingItemDrag(): boolean {
  return pending !== undefined;
}
